import React, { useState, useCallback } from 'react';
import { Plus, Edit2, Trash2, Eye, Bike, FileText, Upload } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Table from '../components/Table';
import Modal from '../components/Modal';
import Form, { FormField, useForm } from '../components/Form';
import FilterBar from '../components/FilterBar';
import { StatusToggleCell, VerifiedToggleCell } from '../components/StatusBadge';
import { useFetch, useFilters } from '../hooks';
import { deliveryBoyService } from '../services/api';
import { formatCurrency } from '../utils/helpers';

const FILTER_INITIAL = { search: '', status: '', verified: '' };
const filterFn = (item, f) => {
  if (f.search && !item.name.toLowerCase().includes(f.search.toLowerCase()) &&
      !(item.mobile || item.phone || '').includes(f.search) &&
      !item.email.toLowerCase().includes(f.search.toLowerCase())) return false;
  if (f.status && item.status !== f.status) return false;
  if (f.verified === 'true' && !item.is_verified) return false;
  if (f.verified === 'false' && item.is_verified) return false;
  return true;
};

const FILTER_FIELDS = [
  { key: 'status', label: 'Status', type: 'select', options: [{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }] },
  { key: 'verified', label: 'Verification', type: 'select', options: [{ value: 'true', label: 'Verified' }, { value: 'false', label: 'Unverified' }] },
];

/* ── File Upload Field ──────────────────────────────────────────────────────── */
const FileUploadField = ({ label, fileKey, file, onChange }) => (
  <FormField label={label}>
    <label className="flex items-center gap-2 cursor-pointer input-field py-2 text-sm text-gray-500 dark:text-gray-400 hover:border-medihub-primary transition-colors">
      <Upload className="w-4 h-4 shrink-0 text-gray-400" />
      <span className="truncate">{file ? file.name : `Upload ${label}…`}</span>
      <input type="file" accept="image/*,.pdf" onChange={e => onChange(fileKey, e.target.files?.[0] || null)} className="hidden" />
    </label>
    {file && (
      <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1 mt-1">
        <FileText className="w-3 h-3" />{file.name}
      </p>
    )}
  </FormField>
);

/* ── Delivery Boy Form ──────────────────────────────────────────────────────── */
const DeliveryBoyForm = ({ initial, onSubmit, onClose }) => {
  const [files, setFiles] = useState({
    adhar_front:   null,
    adhar_back:    null,
    pan_image:     null,
    license_image: null,
  });

  const handleFileChange = (key, file) => setFiles(p => ({ ...p, [key]: file }));

  const rules = {
    name:   { required: true },
    mobile: { required: true, mobile: true },
    email:  { required: true, email: true },
  };

  const { values, errors, handleChange, handleBlur, handleSubmit } = useForm(
    initial ? {
      name:        initial.name        || '',
      mobile:      initial.mobile || initial.phone || '',
      email:       initial.email       || '',
      bike_number: initial.bike_number || '',
    } : {
      name: '', mobile: '', email: '', bike_number: '',
    },
    rules
  );

  const handleFormSubmit = (vals) => {
    onSubmit({ ...vals, ...files });
  };

 const handleBikeNumberChange = (e) => {
   let value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");

   // Normal Number Plate -> UP32AB1234
   const normalRegex = /^[A-Z]{0,2}[0-9]{0,2}[A-Z]{0,2}[0-9]{0,4}$/;

   // BH Series -> 21BH1234AA
   const bhRegex = /^[0-9]{0,2}(BH)?[0-9]{0,4}[A-Z]{0,2}$/;

   // Dono me se koi match hona chahiye
   if (normalRegex.test(value) || bhRegex.test(value)) {
     handleChange({
       target: {
         name: "bike_number",
         value,
       },
     });
   }
 };
  return (
    <Form onSubmit={handleSubmit(handleFormSubmit)}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField label="Full Name" error={errors.name} required>
          <input
            name="name"
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            className="input-field"
            placeholder="e.g. Ravi Kumar"
          />
        </FormField>
        <FormField label="Mobile" error={errors.mobile} required>
          <input
            name="mobile"
            value={values.mobile}
            onChange={handleChange}
            onBlur={handleBlur}
            className="input-field"
            placeholder="9876543210"
          />
        </FormField>
        <FormField label="Email" error={errors.email} required>
          <input
            name="email"
            type="email"
            value={values.email}
            onChange={handleChange}
            onBlur={handleBlur}
            className="input-field"
            placeholder="name@example.com"
          />
        </FormField>
        {/* <FormField label="Bike Number">
          <input name="bike_number" value={values.bike_number} onChange={handleChange} className="input-field" placeholder="e.g. UP32 AB 1234" />
        </FormField> */}
        <FormField label="Bike Number">
          <input
            name="bike_number"
            value={values.bike_number}
            onChange={handleBikeNumberChange}
            className="input-field"
            placeholder="UP32AB1234 / 21BH1234AA"
            maxLength={12}
          />
        </FormField>
      </div>

      {/* Document Uploads */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FileUploadField
          label="Aadhaar Front"
          fileKey="adhar_front"
          file={files.adhar_front}
          onChange={handleFileChange}
        />
        <FileUploadField
          label="Aadhaar Back"
          fileKey="adhar_back"
          file={files.adhar_back}
          onChange={handleFileChange}
        />
        <FileUploadField
          label="PAN Card"
          fileKey="pan_image"
          file={files.pan_image}
          onChange={handleFileChange}
        />
        <FileUploadField
          label="License"
          fileKey="license_image"
          file={files.license_image}
          onChange={handleFileChange}
        />
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="btn-secondary">
          Cancel
        </button>
        <button type="submit" className="btn-primary">
          {initial ? "Update" : "Add Delivery Boy"}
        </button>
      </div>
    </Form>
  );
};

/* ── Page ───────────────────────────────────────────────────────────────────── */
const DeliveryBoys = () => {
  const navigate = useNavigate();
  const { data: boys, loading, refetch } = useFetch(() => deliveryBoyService.getAll());
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const filterFnCb = useCallback(filterFn, []);
  const { filters, onChange: onFilterChange, onReset, filtered, activeCount } = useFilters(FILTER_INITIAL, filterFnCb, boys);

  const handleSave = async (vals) => {
    try {
      if (modal === 'edit') {
        const res = await deliveryBoyService.update(selected.id, vals);
        toast.success(res?.message || 'Delivery boy updated!');
      } else {
        const res = await deliveryBoyService.create(vals);
        toast.success(res?.message || 'Delivery boy added!');
      }
      refetch(); setModal(null); setSelected(null);
    } catch (err) { toast.error(err?.response?.data?.message || 'Something went wrong'); }
  };

  const handleDelete = async () => {
    try {
      const res = await deliveryBoyService.delete(deleteTarget.id);
      toast.success(res?.message || 'Deleted successfully');
      setDeleteTarget(null); refetch();
    } catch (err) { toast.error(err?.response?.data?.message || 'Failed to delete'); }
  };

  const handleToggleStatus = async (boy) => {
    const newStatus = boy.status === 'active' ? 'inactive' : 'active';
    try { await deliveryBoyService.toggleStatus(boy.id, newStatus); toast.success('Status updated'); refetch(); }
    catch { toast.error('Failed'); }
  };

  const handleToggleVerified = async (boy) => {
    const newVerified = boy.is_verified ? 0 : 1;
    try { await deliveryBoyService.toggleVerified(boy.id, newVerified); toast.success('Verification updated'); refetch(); }
    catch { toast.error('Failed'); }
  };

  const columns = [
    {
      key: 'name', label: 'Delivery Boy', render: (v, row) => (
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-orange-100 dark:bg-orange-900/40 rounded-xl flex items-center justify-center shrink-0 text-orange-600 dark:text-orange-400 font-bold text-sm">
            {v[0]}
          </div>
          <div>
            <p className="font-medium text-gray-900 dark:text-white">{v}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{row.email}</p>
          </div>
        </div>
      )
    },
    { key: 'mobile', label: 'Mobile', render: (v, row) => <span className="text-sm text-gray-600 dark:text-gray-400">{v || row.phone}</span> },
    {
      key: 'bike_number', label: 'Bike No.', render: v => v ? (
        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium bg-medihub-light dark:bg-medihub-dark/20 text-medihub-secondary dark:text-medihub-highlight">
          <Bike className="w-3.5 h-3.5" />{v}
        </span>
      ) : <span className="text-xs text-gray-400">—</span>
    },
    {
      key: 'walletBalance', label: 'Wallet', render: v => (
        <span className="font-semibold text-green-700 dark:text-green-400">{formatCurrency(v || 0)}</span>
      )
    },
    {
      key: 'status', label: 'Status', render: (v, row) => (
        <StatusToggleCell active={v === 'active'} onToggle={() => handleToggleStatus(row)} />
      )
    },
    {
      key: 'is_verified', label: 'Verified', render: (v, row) => (
        <VerifiedToggleCell verified={!!v} onToggle={() => handleToggleVerified(row)} />
      )
    },
    {
      key: 'id', label: 'Actions', render: (_, row) => (
        <div className="flex items-center gap-1">
          <button onClick={() => navigate(`/delivery-boys/${row.id}`)} className="p-1.5 hover:bg-medihub-light dark:hover:bg-medihub-dark/20 rounded-lg transition-colors text-medihub-primary" title="View">
            <Eye className="w-4 h-4" />
          </button>
          <button onClick={() => { setSelected(row); setModal('edit'); }} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-500" title="Edit">
            <Edit2 className="w-4 h-4" />
          </button>
          <button onClick={() => setDeleteTarget(row)} className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors text-red-500" title="Delete">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )
    },
  ];

  const activeCount2  = boys?.filter(b => b.status === 'active').length || 0;
  const verifiedCount = boys?.filter(b => b.is_verified).length || 0;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Delivery Partners</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Manage delivery boys and their assignments</p>
        </div>
        <button onClick={() => { setSelected(null); setModal('add'); }} className="btn-primary flex items-center gap-1.5">
          <Plus className="w-4 h-4" />Add Delivery Boy
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total',      value: boys?.length || 0,               color: 'text-gray-900 dark:text-white',        bg: '' },
          { label: 'Active',     value: activeCount2,                    color: 'text-green-700 dark:text-green-400',   bg: 'bg-green-50 dark:bg-green-900/10' },
          { label: 'Verified',   value: verifiedCount,                   color: 'text-medihub-secondary dark:text-medihub-highlight', bg: 'bg-medihub-light dark:bg-medihub-dark/20' },
          { label: 'Unverified', value: (boys?.length || 0) - verifiedCount, color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-900/10' },
        ].map(({ label, value, color, bg }) => (
          <div key={label} className={`card p-4 text-center ${bg}`}>
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      <FilterBar
        filters={filters} onChange={onFilterChange} onReset={onReset}
        searchPlaceholder="Search by name, mobile or email…"
        fields={FILTER_FIELDS} activeCount={activeCount}
      />

      <Table
        title={`Delivery Boys (${filtered.length}${activeCount ? ` of ${boys?.length || 0}` : ''})`}
        columns={columns} data={filtered} loading={loading} exportFilename="delivery-boys"
      />

      <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Delivery Boy" size="sm">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Are you sure you want to delete <span className="font-semibold text-gray-900 dark:text-white">{deleteTarget?.name}</span>? This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3 mt-5">
          <button onClick={() => setDeleteTarget(null)} className="btn-secondary">Cancel</button>
          <button onClick={handleDelete} className="btn-danger">Delete</button>
        </div>
      </Modal>

      <Modal
        isOpen={modal === 'add' || modal === 'edit'}
        onClose={() => { setModal(null); setSelected(null); }}
        title={modal === 'edit' ? 'Edit Delivery Boy' : 'Add Delivery Boy'}
        size="lg"
      >
        <DeliveryBoyForm
          initial={modal === 'edit' ? selected : null}
          onSubmit={handleSave}
          onClose={() => { setModal(null); setSelected(null); }}
        />
      </Modal>
    </div>
  );
};

export default DeliveryBoys;
